import React, { useState, useEffect, useRef } from 'react';
import { Accessibility, X, Type, Contrast, Link2, RotateCcw } from 'lucide-react';

const STORAGE_KEY = 'gb_a11y_settings';
const DEFAULTS = {
  fontScale: 1,
  highContrast: false,
  highlightLinks: false,
  readableFont: false,
  reducedMotion: false,
};

const A11yWidget = () => {
  const [open, setOpen] = useState(false);
  const [settings, setSettings] = useState(() => {
    try {
      return { ...DEFAULTS, ...JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}') };
    } catch {
      return DEFAULTS;
    }
  });
  const panelRef = useRef(null);
  const triggerRef = useRef(null);
  const firstFocusableRef = useRef(null);

  // Apply settings to DOM
  useEffect(() => {
    const root = document.documentElement;
    root.style.fontSize = `${100 * settings.fontScale}%`;
    root.classList.toggle('a11y-high-contrast', settings.highContrast);
    root.classList.toggle('a11y-highlight-links', settings.highlightLinks);
    root.classList.toggle('a11y-readable-font', settings.readableFont);
    root.classList.toggle('a11y-reduced-motion', settings.reducedMotion);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  }, [settings]);

  // Close on Esc + focus trap
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
      if (e.key === 'Tab' && panelRef.current) {
        const focusables = panelRef.current.querySelectorAll(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener('keydown', onKey);
    setTimeout(() => firstFocusableRef.current?.focus(), 60);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const update = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
  const reset = () => setSettings(DEFAULTS);

  const fontSteps = [
    { v: 0.9, label: 'קטן' },
    { v: 1, label: 'רגיל' },
    { v: 1.15, label: 'גדול' },
    { v: 1.3, label: 'גדול מאוד' },
  ];

  return (
    <>
      {/* Trigger button */}
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen(true)}
        aria-label="פתח תפריט נגישות"
        aria-expanded={open}
        aria-controls="a11y-panel"
        className="fixed bottom-5 left-5 z-[60] w-14 h-14 bg-navy text-cream flex items-center justify-center shadow-xl hover:bg-navy-700 transition-colors rounded-full border-2 border-bronze"
      >
        <Accessibility className="w-6 h-6" aria-hidden="true" />
      </button>

      {/* Backdrop */}
      {open && (
        <div
          className="fixed inset-0 bg-navy/50 z-[59]"
          onClick={() => setOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Panel */}
      <div
        id="a11y-panel"
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="a11y-title"
        hidden={!open}
        className="fixed bottom-5 left-5 z-[60] w-[calc(100vw-2.5rem)] sm:w-[380px] bg-cream border-2 border-navy shadow-2xl"
      >
        <div className="flex items-center justify-between bg-navy text-cream p-4">
          <div className="flex items-center gap-2">
            <Accessibility className="w-5 h-5 text-bronze" aria-hidden="true" />
            <h2 id="a11y-title" className="font-serif text-lg">תפריט נגישות</h2>
          </div>
          <button
            ref={firstFocusableRef}
            type="button"
            onClick={() => {
              setOpen(false);
              triggerRef.current?.focus();
            }}
            aria-label="סגור תפריט נגישות"
            className="text-cream/80 hover:text-bronze p-1"
          >
            <X className="w-5 h-5" aria-hidden="true" />
          </button>
        </div>

        <div className="p-5 space-y-5 max-h-[70vh] overflow-y-auto">
          {/* Font size */}
          <fieldset className="border-0 p-0 m-0">
            <legend className="flex items-center gap-2 eyebrow text-navy/70 mb-3">
              <Type className="w-4 h-4" aria-hidden="true" /> גודל טקסט
            </legend>
            <div className="grid grid-cols-4 gap-2" role="radiogroup" aria-label="גודל טקסט">
              {fontSteps.map((s) => (
                <button
                  key={s.v}
                  type="button"
                  role="radio"
                  aria-checked={settings.fontScale === s.v}
                  onClick={() => update('fontScale', s.v)}
                  className={`px-2 py-2 text-sm border-2 transition-colors ${
                    settings.fontScale === s.v
                      ? 'bg-navy text-cream border-navy'
                      : 'bg-cream text-navy border-navy/20 hover:border-navy'
                  }`}
                >
                  {s.label}
                </button>
              ))}
            </div>
          </fieldset>

          {/* Toggles */}
          <div className="space-y-2">
            <ToggleRow
              icon={Contrast}
              label="ניגודיות גבוהה"
              checked={settings.highContrast}
              onChange={(v) => update('highContrast', v)}
            />
            <ToggleRow
              icon={Link2}
              label="הדגשת קישורים"
              checked={settings.highlightLinks}
              onChange={(v) => update('highlightLinks', v)}
            />
            <ToggleRow
              icon={Type}
              label="פונט קריא"
              checked={settings.readableFont}
              onChange={(v) => update('readableFont', v)}
            />
            <ToggleRow
              icon={RotateCcw}
              label="הפחתת אנימציות"
              checked={settings.reducedMotion}
              onChange={(v) => update('reducedMotion', v)}
            />
          </div>

          {/* Reset */}
          <button
            type="button"
            onClick={reset}
            className="w-full bg-cream-200 hover:bg-cream-300 text-navy py-3 text-sm flex items-center justify-center gap-2 border border-navy/20"
          >
            <RotateCcw className="w-4 h-4" aria-hidden="true" />
            איפוס הגדרות
          </button>

          {/* Statement / contact */}
          <div className="text-xs text-navy/60 leading-relaxed pt-3 border-t border-navy/10">
            <p>
              האתר עומד בתקן הישראלי 5568 ברמה AA (WCAG 2.1).
              לשאלות נגישות פנו אל{' '}
              <a href="mailto:office@gal-bl.co.il" className="text-bronze underline">
                office@gal-bl.co.il
              </a>
            </p>
          </div>
        </div>
      </div>
    </>
  );
};

const ToggleRow = ({ icon: Icon, label, checked, onChange }) => {
  const id = `a11y-${label.replace(/\s+/g, '-')}`;
  return (
    <button
      type="button"
      id={id}
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="w-full flex items-center justify-between p-3 border border-navy/15 hover:border-navy text-right"
    >
      <span className="flex items-center gap-3 text-navy text-sm">
        <Icon className="w-4 h-4 text-bronze" aria-hidden="true" />
        {label}
      </span>
      <span
        className={`w-10 h-6 rounded-full transition-colors flex-shrink-0 relative ${
          checked ? 'bg-navy' : 'bg-navy/20'
        }`}
        aria-hidden="true"
      >
        <span
          className={`absolute top-0.5 w-5 h-5 bg-cream rounded-full transition-all ${
            checked ? 'right-0.5' : 'right-[18px]'
          }`}
        />
      </span>
    </button>
  );
};

export default A11yWidget;
